import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";

import { useNavigate, useParams } from "react-router-dom";
import { Default } from "../../layouts/default";
import NotFound from "../page-not-found";
import "../../../css/facilityDetail.css";
import "../../../css/hotels.css";
import HotelDetailView from "./HotelDetailView";

const API_URL = "https://admin.bookvenue.app/api/hotels";

const HotelDetail = () => {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [hotel, setHotel] = useState(null);
  const [activeTab, setActiveTab] = useState("about");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    setActiveTab("about");
    setHotel(null);
    setError("");
  }, [slug]);

  useEffect(() => {
    const controller = new AbortController();

    const fetchHotel = async () => {
      try {
        setLoading(true);
        setError("");
        setHotel(null);

        const response = await fetch(`${API_URL}/${encodeURIComponent(slug)}`, {
          method: "GET",
          headers: {
            Accept: "application/json",
          },
          signal: controller.signal,
        });

        if (response.status === 404) {
          setHotel(null);
          return;
        }

        if (!response.ok) {
          throw new Error(`Unable to load hotel (${response.status})`);
        }

        const result = await response.json();
        const data = result?.data || result;

        if (!data || typeof data !== "object") {
          throw new Error("Invalid hotel data received from server.");
        }

        const rooms = Array.isArray(data.rooms)
          ? data.rooms.map((room) => {
              const adults = Number(room.max_adults) || 0;
              const children = Number(room.max_children) || 0;

              const capacity =
                Number(room.max_guests) ||
                adults + children ||
                Number(room.capacity) ||
                4;

              const price =
                Number(room.starting_price) ||
                Number(room.base_price) ||
                Number(room.price) ||
                0;

              return {
                ...room,
                price,
                capacity,
                image: room.featured_image || room.image || null,
                images: Array.isArray(room.images)
                  ? room.images.filter(Boolean)
                  : [],
              };
            })
          : [];

        let rating = null;
        let reviewCount = 0;

        if (typeof data.rating === "object" && data.rating !== null) {
          rating =
            data.rating.average !== undefined
              ? Number(data.rating.average)
              : null;

          reviewCount = Number(data.rating.count) || 0;
        } else if (
          data.rating !== undefined &&
          data.rating !== null &&
          data.rating !== ""
        ) {
          rating = Number(data.rating) || 0;
        }

        if (!reviewCount && Array.isArray(data.reviews)) {
          reviewCount = data.reviews.length;
        }

        const normalizedHotel = {
          ...data,

          image: data.cover_image || data.image || null,

          images: Array.isArray(data.gallery)
            ? data.gallery.filter(Boolean)
            : Array.isArray(data.images)
              ? data.images.filter(Boolean)
              : [],

          category:
            data.badge || data.type?.name || data.types?.[0]?.name || "Hotel",

          price:
            Number(data.starting_price) ||
            rooms?.[0]?.price ||
            Number(data.price) ||
            0,

          rating,
          reviewCount,

          checkInTime: data.check_in_label || data.check_in || "12:00 PM",

          checkOutTime: data.check_out_label || data.check_out || "11:00 AM",

          amenities: Array.isArray(data.amenities) ? data.amenities : [],

          rooms,

          reviews: Array.isArray(data.reviews) ? data.reviews : [],
        };

        setHotel(normalizedHotel);
      } catch (err) {
        if (err?.name === "AbortError") {
          return;
        }

        setError(
          err?.message || "Something went wrong while loading the hotel.",
        );
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    if (slug) {
      fetchHotel();
    } else {
      setLoading(false);
      setHotel(null);
    }

    return () => {
      controller.abort();
    };
  }, [slug]);

  if (loading) {
    return (
      <Default>
        <div className="af-empty">
          <h3>Loading hotel...</h3>
        </div>
      </Default>
    );
  }

  if (error) {
    return (
      <Default>
        <div className="af-empty">
          <h3>Unable to load hotel</h3>
          <p>{error}</p>
        </div>
      </Default>
    );
  }

  if (!hotel) {
    return <NotFound />;
  }

  return (
    <Default>
      <Helmet>
        <title>{hotel.name || "Hotel"} | Hotels | Book Venue</title>

        <meta
          name="description"
          content={
            hotel.description?.slice(0, 155) ||
            `Book ${hotel.name || "Hotel"} on Book Venue.`
          }
        />

        <meta name="robots" content="index, follow" />

        <link
          rel="canonical"
          href={`https://bookvenue.app/hotels/${hotel.slug || slug}`}
        />
      </Helmet>

      <HotelDetailView
        hotel={hotel}
        nearbyHotels={[]}
        navigate={navigate}
        activeTab={activeTab}
        onTabClick={setActiveTab}
      />
    </Default>
  );
};

export default HotelDetail;
